import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUpRight, Check, Focus, PenTool, Sparkles } from 'lucide-react';
import { gsap, useGSAP } from '@/lib/gsap-config';
import './blueprint-studio.css';

const chartDraft = 'M0 130C50 130 80 130 120 130S200 130 240 130S310 130 360 130';
const chartFinal = 'M0 140C45 140 75 100 120 110S195 55 240 65S310 15 360 10';

// Compact local image sequence: render once, then only draw the nearest frame on scroll.
function buildBlueprintFrames() {
  return Array.from({ length: 32 }, (_, frame) => {
    const image = document.createElement('canvas');
    image.width = 480; image.height = 300;
    const ctx = image.getContext('2d');
    if (!ctx) return image;
    const progress = frame / 31;
    ctx.strokeStyle = '#77afcc28';
    ctx.lineWidth = 0.6;
    for (let x = 0; x <= 480; x += 20) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, 300); ctx.stroke();
    }
    for (let y = 0; y <= 300; y += 20) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(480, y); ctx.stroke();
    }
    for (let layer = 0; layer < 7; layer++) {
      const inset = layer * (5 + 6 * progress);
      ctx.strokeStyle = `rgba(137, 202, 224, ${0.18 - layer * 0.02})`;
      ctx.strokeRect(25 + inset, 25 + inset * 0.6, 430 - inset * 2, 250 - inset * 1.2);
    }
    const x = progress * 600 - 60;
    const glow = ctx.createLinearGradient(x - 35, 0, x + 35, 0);
    glow.addColorStop(0, '#94d9ec00'); glow.addColorStop(0.5, '#94d9ec20'); glow.addColorStop(1, '#94d9ec00');
    ctx.fillStyle = glow; ctx.fillRect(x - 35, 0, 70, 300);
    return image;
  });
}

export function BlueprintTo3DSection() {
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const route = useRef<SVGPathElement>(null);

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add({ motion: '(prefers-reduced-motion: no-preference)', desktop: '(min-width: 1000px) and (min-height: 760px)' }, (context) => {
      if (!context.conditions?.motion || !root.current) return;
      const desktop = !!context.conditions.desktop;
      const phases = Array.from(root.current.querySelectorAll<HTMLElement>('.bs-step'));
      const frames = buildBlueprintFrames();
      const ctx = canvas.current?.getContext('2d');
      const playhead = { frame: 0 };
      let previousFrame = -1;
      let previousPhase = -1;
      const paint = () => {
        const frame = Math.round(playhead.frame);
        if (!ctx || frame === previousFrame) return;
        previousFrame = frame;
        ctx.clearRect(0, 0, 480, 300);
        ctx.drawImage(frames[frame], 0, 0);
      };
      paint();
      const tl = gsap.timeline({
        defaults: { ease: 'power2.inOut' },
        scrollTrigger: {
          trigger: stage.current, start: desktop ? 'top 100px' : 'top 80%',
          end: desktop ? '+=1200' : 'bottom 35%',
          pin: desktop, pinSpacing: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true,
        },
        onUpdate: () => {
          const phase = tl.progress() < 0.32 ? 0 : tl.progress() < 0.68 ? 1 : 2;
          if (phase === previousPhase) return;
          previousPhase = phase;
          phases.forEach((node, index) => {
            node.dataset.active = String(index === phase);
            if (index === phase) node.setAttribute('aria-current', 'step');
            else node.removeAttribute('aria-current');
          });
        },
      });
      tl.fromTo('.bs-draft-frame', { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 1.2 }, 0)
        .fromTo('.bs-draft-block', { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 1.4, stagger: 0.12 }, 0.2)
        .fromTo(route.current, { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 1.9, ease: 'none' }, 0.1)
        .fromTo('.bs-probe', { opacity: 0 }, { opacity: 1, duration: 0.2 }, 0.1)
        .to('.bs-probe', { motionPath: { path: route.current!, align: route.current!, alignOrigin: [0.5, 0.5] }, duration: 1.9, ease: 'none' }, 0.1)
        .fromTo('.bs-draft-chart', { morphSVG: chartDraft }, { morphSVG: chartFinal, duration: 1.3 }, 1.1)
        .to('.bs-probe', { opacity: 0, duration: 0.3 }, 2)
        .fromTo('.bs-finished', { clipPath: 'circle(0% at 72% 62%)' }, { clipPath: 'circle(135% at 72% 62%)', duration: 1.9 }, 1.8)
        .fromTo('.bs-chart-line', { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 1.3 }, 2.2)
        .fromTo('.bs-chart-area', { opacity: 0 }, { opacity: 1, duration: 0.9 }, 2.8)
        .fromTo('.bs-metric', { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, stagger: 0.12 }, 2.7)
        .fromTo('.bs-output', { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, 3.5)
        .to(playhead, { frame: frames.length - 1, duration: 4.8, ease: 'none', onUpdate: paint }, 0)
        .fromTo('.bs-progress', { scaleX: 0 }, { scaleX: 1, duration: 4.8, ease: 'none' }, 0);
      return () => {
        phases.forEach((node, index) => {
          node.dataset.active = String(index === 2);
          if (index === 2) node.setAttribute('aria-current', 'step');
          else node.removeAttribute('aria-current');
        });
        frames.forEach((frame) => { frame.width = 0; frame.height = 0; });
        ctx?.clearRect(0, 0, 480, 300);
      };
    });
    return () => media.revert();
  }, { scope: root, dependencies: [i18n.resolvedLanguage], revertOnUpdate: true });

  return (
    <section ref={root} className="blueprint-studio" aria-labelledby="bs-title">
      <div className="bs-container">
        <header className="bs-heading"><p className="bs-eyebrow">[{t('blueprint.act')}]</p><div><h2 id="bs-title">{t('blueprint.title')}<span>{t('blueprint.titleAccent')}</span></h2><p className="bs-intro">{t('blueprint.intro')}</p></div></header>
        <div ref={stage} className="bs-workbench">
          <aside className="bs-sidebar" aria-label={t('blueprint.processLabel')}>
            <div className="bs-sidebar-title"><Focus size={15} aria-hidden="true" />{t('blueprint.studio')}</div>
            <ol className="bs-steps">{[PenTool, Sparkles, Check].map((Icon, index) => <li key={index} className="bs-step" data-active={index === 2 ? 'true' : 'false'} aria-current={index === 2 ? 'step' : undefined}><span className="bs-step-icon"><Icon size={15} aria-hidden="true" /></span><div><span className="bs-step-number">0{index + 1}</span><h3>{t(`blueprint.steps.${index}.title`)}</h3><p>{t(`blueprint.steps.${index}.description`)}</p></div></li>)}</ol>
            <div className="bs-sidebar-note"><span>16:9</span><p>{t('blueprint.sidebarNote')}</p></div>
          </aside>
          <div className="bs-main">
            <div className="bs-toolbar"><span><span className="bs-status-dot" />{t('blueprint.canvasLabel')}</span><span>{t('blueprint.sampleLabel')} / 01</span></div>
            <div className="bs-viewport">
              <canvas ref={canvas} width={480} height={300} className="bs-sequence" aria-hidden="true" />
              <span className="bs-dimension" aria-hidden="true">1920 × 1080 <span>—</span> 16:9</span>
              <div className="bs-slide">
                <svg className="bs-draft" viewBox="0 0 800 450" fill="none" aria-hidden="true">
                  <path className="bs-draft-frame" d="M12 12H788V438H12Z" /><path className="bs-draft-block" d="M40 56H760M40 88H480V160H40ZM40 190H270V360H40ZM310 190H760V360H310ZM40 402H760" />
                  <path ref={route} className="bs-draft-route" d="M40 56H760V402H40V190H270V360H310V190H760" />
                  <g transform="translate(350 210)"><path className="bs-draft-chart" d={chartDraft} /></g>
                  <g className="bs-probe"><circle r="12" fill="#a8e8f1" opacity=".15" /><circle r="3" fill="#c4f7f9" /><path d="M-16 0H16M0-16V16" stroke="#a8e8f1" strokeWidth=".7" /></g>
                </svg>
                <article className="bs-finished">
                  <div className="bs-slide-top"><span>QUARTERLY / {t('blueprint.reportLabel')}</span><span>2026 — Q4</span></div>
                  <div className="bs-slide-title"><span>{t('blueprint.slideEyebrow')}</span><h3>{t('blueprint.reportTitle')}</h3></div>
                  <div className="bs-slide-content">
                    <div className="bs-summary"><span>{t('blueprint.revenue')}</span><strong>$4.8<span>M</span></strong><p>{t('blueprint.reportDescription')}</p><div className="bs-metric"><ArrowUpRight size={14} aria-hidden="true" /><strong>+182%</strong><span>{t('blueprint.growthShort')}</span></div></div>
                    <div className="bs-chart"><div><span>{t('blueprint.chartTitle')}</span><span>2025 — 2026</span></div><svg viewBox="0 0 400 180" role="img" aria-label={t('blueprint.chartDescription')}><defs><linearGradient id="bs-chart-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#268377" stopOpacity=".25" /><stop offset="1" stopColor="#268377" stopOpacity="0" /></linearGradient></defs><path d="M20 20H380M20 65H380M20 110H380M20 155H380" stroke="#244e4220" fill="none" /><g transform="translate(20 5)"><path className="bs-chart-area" d={`${chartFinal}L360 155H0Z`} fill="url(#bs-chart-fill)" /><path className="bs-chart-line" d={chartFinal} stroke="#257a69" strokeWidth="3" fill="none" /><circle cx="360" cy="10" r="5" fill="#257a69" stroke="#f1f2e8" strokeWidth="3" /></g></svg><div className="bs-chart-axis"><span>Q1</span><span>Q2</span><span>Q3</span><span>Q4</span></div></div>
                  </div>
                  <div className="bs-slide-bottom"><span>{t('blueprint.sampleData')}</span><span>LAMSLIDE / 01</span></div>
                </article>
              </div>
              <div className="bs-output"><span><Check size={12} aria-hidden="true" /></span>{t('blueprint.outputReady')}<span>.PPTX</span></div>
            </div>
            <div className="bs-bottom"><span><ArrowDown size={12} aria-hidden="true" />{t('blueprint.scrollHint')}</span><span>{t('blueprint.editable')}</span></div>
            <div className="bs-progress" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}
export default BlueprintTo3DSection;
